
import { useIsMobile } from "@/hooks/use-mobile";
import { toast } from "@/hooks/use-toast";
import { FixedJob, useFixedJobSearch } from "@/hooks/useFixedJobSearch";
import { useState } from "react";
import { Control, Controller, Path, UseFormSetValue } from "react-hook-form";
import Select from "react-select";
import FieldWrapper from "../fields/FieldWrapper";
import { customStyles } from "../SelectStyles";

interface FixedJobSearchFieldProps<TFieldValues extends Record<string, any>> {
  control: Control<TFieldValues>;
  setValue: UseFormSetValue<TFieldValues>;
  name: Path<TFieldValues>;
  targetField?: Path<TFieldValues>;
  teacherCode?: string;
}

const dayTranslation = {
  "monday": "จันทร์",
  "tuesday": "อังคาร",
  "wednesday": "พุธ",
  "thursday": "พฤหัส",
  "friday": "ศุกร์",
  "saturday": "เสาร์",
  "sunday": "อาทิตย์"
}

export const FixedJobSearchField = <TFieldValues extends Record<string, any>>({
  control,
  setValue,
  name,
  targetField,
  teacherCode
}: FixedJobSearchFieldProps<TFieldValues>) => {

  const [selectedFixedJob, setSelectedFixedJob] = useState<FixedJob | null>(null);
  const isMobile = useIsMobile();

  const {
    fixedJobs,
    isLoading
  } = useFixedJobSearch(teacherCode);

  // Handle fixed job selection
  const handleSelectFixedJob = (option: any) => {
    if (!option) {
      handleClearSelection();
      return;
    }
    const fixedJob = option.fixedJob;
    setSelectedFixedJob(fixedJob);
    setValue(name, fixedJob);

    if (targetField) {
      setValue(targetField, fixedJob);
    }

    toast({
      title: "ตารางประจำที่เลือก",
      description: `${fixedJob.student_names}: ${dayTranslation[fixedJob.time_slot_day]} ${fixedJob.class_type} ${fixedJob.subjects} ${fixedJob.education_level}`,
    });
  };

  // Handle clearing selection
  const handleClearSelection = () => {
    setSelectedFixedJob(null);
    setValue(name, undefined);
    if (targetField) {
      setValue(targetField, undefined);
    }
  };
  
  const mapFixedJob = (fixedJob: FixedJob) => ({
    value: fixedJob.time_slot_id,
    label: `${fixedJob.student_names}: ${dayTranslation[fixedJob.time_slot_day]} ${fixedJob.class_type} ${fixedJob.subjects} ${fixedJob.education_level}`,
    fixedJob: fixedJob,
    timeSlotId: fixedJob.time_slot_id,
    studentNames: fixedJob.student_names,
    studentCodes: fixedJob.student_codes,
    subjects: fixedJob.subjects,
    educationLevel: fixedJob.education_level,
    classType: fixedJob.class_type,
    timeSlotDay: fixedJob.time_slot_day,
  })

  // Convert students array to options format for react-select
  const options = fixedJobs.map(mapFixedJob);

  return (
    <FieldWrapper element={{ id: name, name, type: "select", label: "ค้นหาตารางประจำ", required: true }}>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Select
            options={options}
            value={selectedFixedJob ? mapFixedJob(selectedFixedJob) : null}
            onChange={handleSelectFixedJob}
            isLoading={isLoading}
            isClearable={true}
            placeholder="ค้นหาข้อมูลตารางประจำ..."
            styles={customStyles}
            className="react-select-container"
            classNamePrefix="react-select"
            menuPlacement={isMobile ? "auto" : "bottom"}
            menuPosition="fixed"
            ref={field.ref}
            name={field.name}
            onBlur={field.onBlur}
          />
        )}
      />
    </FieldWrapper>
  );
};

export default FixedJobSearchField;
